import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { ArrowLeft, Info } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";

export const Route = createFileRoute("/forest-bathing")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Forest Bathing — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle, trauma-informed guide to forest bathing (shinrin-yoku) — slow down and use your senses to feel more present with nature.",
      },
      { property: "og:title", content: "Forest Bathing — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Swipeable cards for slow walking, sensory awareness and quiet rest among trees.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/forest-bathing" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://recoverytrauma.com/forest-bathing",
      },
    ],
  }),
  component: withMemberGate(ForestBathingPage, "Forest Bathing"),
});

type Card = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  ideas: string[];
  tint: string;
};

const CARDS: Card[] = [
  {
    id: "slow-walk",
    emoji: "🚶",
    title: "Slow Walk",
    description: "Half the pace, twice the noticing.",
    ideas: [
      "Take ten very slow steps",
      "Let curiosity choose the direction",
      "Stop whenever you want to look",
    ],
    tint: "#7BAE7F",
  },
  {
    id: "five-senses",
    emoji: "👀",
    title: "Five Senses",
    description: "A gentle sensory inventory among trees.",
    ideas: [
      "5 things you can see",
      "4 things you can hear",
      "3 things you can touch",
      "2 things you can smell, 1 you can taste (a sip of water)",
    ],
    tint: "#9DAF60",
  },
  {
    id: "tree-time",
    emoji: "🌳",
    title: "Tree Time",
    description: "Spend a few minutes with one tree.",
    ideas: [
      "Notice bark, leaves, light",
      "Place a hand on the trunk if it feels okay",
      "Imagine the roots beneath you",
    ],
    tint: "#5E8A60",
  },
  {
    id: "sit-spot",
    emoji: "🪨",
    title: "Sit Spot",
    description: "Choose a place and simply stay a while.",
    ideas: [
      "Sit for 5–10 minutes",
      "Let your eyes soften",
      "Notice what arrives and what passes",
    ],
    tint: "#C9A66B",
  },
];

const REFLECTION = [
  "What did you notice in your body?",
  "Was there a moment that felt nourishing?",
  "Is there something from the forest you'd like to carry with you?",
];

function ForestBathingPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="mb-6">
        <Link
          to="/holistic-wellbeing"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Wellbeing Hub
        </Link>
      </div>

      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <span aria-hidden>🍃</span> Forest Bathing
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          Slow down and immerse yourself in nature
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Gentle, sensory practices inspired by shinrin-yoku — no destination,
          no goal, just being.
        </p>
      </header>

      <div className="mb-8 flex items-start gap-3 rounded-2xl border border-gold/30 bg-cream/40 p-4 text-sm text-foreground/85">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
        <p>
          Educational only — not therapy. If anything feels overwhelming, pause
          and reach out to{" "}
          <Link to="/crisis" className="font-semibold underline">
            crisis resources
          </Link>
          .
        </p>
      </div>

      <Carousel
        opts={{ align: "start", loop: true }}
        className="w-full"
        aria-label="Forest bathing practices"
      >
        <CarouselSwipeHint label="Swipe to explore" storageKey="forest-bathing" />
        <CarouselContent className="-ms-5">
          {CARDS.map((c) => (
            <CarouselItem
              key={c.id}
              className="basis-[88%] ps-5 sm:basis-1/2 lg:basis-1/2"
            >
              <article className="group relative flex h-full flex-col rounded-[2rem] border border-gold-light/60 bg-gradient-to-br from-[oklch(0.97_0.025_85)] via-card to-[oklch(0.93_0.05_82)] p-6 shadow-[0_24px_60px_-32px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] transition-all duration-300 hover:-translate-y-1 dark:border-gold-light/40 dark:from-[oklch(0.30_0.015_78)] dark:via-card dark:to-[oklch(0.34_0.04_80)] sm:p-8">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  <span aria-hidden>🍃</span> Forest practice
                </div>
                <div className="mt-3 flex items-start gap-4">
                  <div
                    aria-hidden
                    className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl text-5xl ring-1 ring-gold-light/50 sm:h-28 sm:w-28 sm:text-6xl"
                    style={{
                      background: `radial-gradient(ellipse at center, color-mix(in oklab, ${c.tint} 30%, var(--cream)) 0%, color-mix(in oklab, ${c.tint} 12%, var(--cream)) 75%, transparent 100%)`,
                    }}
                  >
                    <span>{c.emoji}</span>
                  </div>
                  <div className="min-w-0 pt-1">
                    <h2 className="font-display text-xl leading-tight text-foreground sm:text-2xl">
                      {c.title}
                    </h2>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground/80 sm:text-base">
                  {c.description}
                </p>
                <div className="mt-5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                    Ideas
                  </div>
                  <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                    {c.ideas.map((idea) => (
                      <li key={idea} className="flex items-start gap-2">
                        <span
                          aria-hidden
                          className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]"
                        />
                        <span>{idea}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-6">
                  <div className="rounded-2xl border border-gold/30 bg-cream/50 p-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                      <span aria-hidden>💛</span> Gentle reflection
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-foreground/85 sm:text-sm">
                      {REFLECTION.map((p) => (
                        <li key={p} className="flex items-start gap-2">
                          <span aria-hidden className="text-[var(--gold-deep)]">
                            •
                          </span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/journal"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] hover:underline"
                    >
                      Capture in Journal →
                    </Link>
                  </div>
                </div>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="-start-3 hidden sm:flex" />
        <CarouselNext className="-end-3 hidden sm:flex" />
      </Carousel>
    </div>
  );
}
